import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store";
import { signInWithCustomTokenThunk } from "../../../../redux/authSlice";

const CLAIM_API = "https://europe-west3-metarelm.cloudfunctions.net/claim";

// ── Ahome design tokens ───────────────────────────────────
const T = {
  bg:        "#FFFFFF",
  bgSoft:    "#FAFAFA",
  bgMuted:   "#F4F4F2",
  black:     "#0A0A0A",
  blackMid:  "#1C1C1C",
  gold:      "#C9A96E",
  goldLight: "#F0E2C8",
  goldBg:    "#FBF6EE",
  red:       "#e84020",
  muted:     "#8A8A86",
  mutedSoft: "#B8B8B4",
  border:    "#E6E6E4",
  borderDk:  "#C8C8C4",
};

type Mode  = "seller" | "product";
type Phase = "form" | "loading" | "success" | "error";

const Claim: React.FC = () => {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();

  const token      = params.get("token") || "";
  const modeHint   = params.get("mode") === "product" ? "product" : "seller";

  const [phase,    setPhase]    = useState<Phase>(token ? "form" : "error");
  const [mode,     setMode]     = useState<Mode>(modeHint as Mode);
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [errMsg,   setErrMsg]   = useState(token ? "" : "Invalid or missing claim link.");
  const [focused,  setFocused]  = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const canSubmit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 6;

  const handleClaim = async () => {
    if (!canSubmit) return;
    setPhase("loading");
    setErrMsg("");
    try {
      const res  = await fetch(CLAIM_API, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, email, password, mode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Something went wrong");
      await dispatch(signInWithCustomTokenThunk(data.customToken));
      setPhase("success");
    } catch (err: any) {
      setErrMsg(err.message || "Failed to claim. Please try again.");
      setPhase("form");
    }
  };

  const isSeller = mode === "seller";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0A0A0A; }
        ::placeholder { color: ${T.mutedSoft}; font-family: 'Montserrat', sans-serif; }
        ::-webkit-scrollbar { display: none; }

        .ce-input {
          width: 100%;
          padding: 13px 14px;
          border: 1px solid ${T.border};
          background: ${T.bgSoft};
          color: ${T.black};
          font-family: 'Montserrat', sans-serif;
          font-size: 13px;
          outline: none;
          transition: border-color .2s, box-shadow .2s;
        }
        .ce-input:focus {
          border-color: ${T.black};
          box-shadow: none;
        }

        .ce-mode-btn {
          flex: 1;
          padding: 14px 12px;
          border: 1px solid ${T.border};
          background: ${T.bgSoft};
          cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.2em;
          color: ${T.muted};
          transition: all .2s;
          text-transform: uppercase;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .ce-mode-btn.active {
          background: ${T.black};
          border-color: ${T.black};
          color: ${T.bg};
        }
        .ce-mode-btn:not(.active):hover {
          border-color: ${T.borderDk};
          color: ${T.blackMid};
        }

        .ce-primary-btn {
          width: 100%;
          padding: 15px;
          background: ${T.black};
          color: #fff;
          border: none;
          cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.24em;
          transition: opacity .2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .ce-primary-btn:disabled {
          background: ${T.border};
          color: ${T.mutedSoft};
          cursor: not-allowed;
        }
        .ce-primary-btn:not(:disabled):hover { opacity: 0.85; }

        .ce-ghost-btn {
          width: 100%;
          padding: 13px;
          background: transparent;
          color: ${T.muted};
          border: 1px solid ${T.border};
          cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.2em;
          transition: all .2s;
        }
        .ce-ghost-btn:hover { border-color: ${T.black}; color: ${T.black}; }

        .ce-check-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        @keyframes ce-spin { to { transform: rotate(360deg); } }
        @keyframes ce-fade { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform:none; } }
        .ce-fade { animation: ce-fade .45s ease both; }

        .ce-progress-bar {
          height: 2px;
          background: ${T.border};
          overflow: hidden;
        }
        .ce-progress-fill {
          height: 100%;
          background: ${T.black};
          animation: ce-progress 1.6s ease-in-out infinite;
        }
        @keyframes ce-progress {
          0%   { width: 0%;   margin-left: 0%; }
          50%  { width: 60%;  margin-left: 20%; }
          100% { width: 0%;   margin-left: 100%; }
        }

        .ce-tick {
          width: 20px; height: 20px;
          background: ${T.black};
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }

        .ce-pw-toggle {
          position: absolute; right: 14px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: ${T.muted}; font-family: 'Montserrat', sans-serif;
          font-size: 9px; font-weight: 700; letter-spacing: 0.1em;
          padding: 4px;
        }
        .ce-pw-toggle:hover { color: ${T.black}; }
      `}</style>

      {/* ── OUTER WRAPPER ── */}
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #0A0A0A 0%, #1C1C1C 50%, #0A0A0A 100%)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: isMobile ? "16px" : "48px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: 520 }}>

          {/* ── TOP NAV ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <button
              onClick={() => navigate("/")}
              style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,.35)", fontFamily: "'Montserrat', sans-serif", fontSize: 10, letterSpacing: "0.16em", display: "flex", alignItems: "center", gap: 6 }}
            >
              ← HOME
            </button>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, letterSpacing: "0.35em", color: "#FFFFFF" }}>
              ARELUXE
            </div>
            <div style={{ width: 60 }} />
          </div>

          {/* ── CARD ── */}
          <div style={{ background: T.bg, position: "relative", overflow: "hidden" }}>

            {/* Gold top accent */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${T.gold}, #d46b10, ${T.red})` }} />

            {/* Header strip */}
            <div style={{ background: T.bgMuted, padding: isMobile ? "28px 24px 22px" : "32px 36px 26px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.28em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 10 }}>
                ELECTRONICS MARKETPLACE
              </div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 28 : 36, fontWeight: 300, color: T.black, letterSpacing: "0.04em", lineHeight: 1.1, fontStyle: "italic" }}>
                {phase === "success"
                  ? isSeller ? "Shop is live." : "Product is live."
                  : phase === "error"
                    ? "Link expired."
                    : isSeller ? "Claim your shop." : "Claim your product."
                }
              </h1>
            </div>

            {/* Progress bar when loading */}
            {phase === "loading" && <div className="ce-progress-bar"><div className="ce-progress-fill" /></div>}

            {/* ── BODY ── */}
            <div style={{ padding: isMobile ? "24px 24px 32px" : "32px 36px 40px" }}>

              {/* ── LOADING ── */}
              {phase === "loading" && (
                <div className="ce-fade" style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ width: 36, height: 36, border: `2px solid ${T.border}`, borderTop: `2px solid ${T.black}`, borderRadius: "50%", animation: "ce-spin 0.9s linear infinite", margin: "0 auto 20px" }} />
                  <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", color: T.muted }}>
                    SETTING UP YOUR ACCOUNT…
                  </p>
                </div>
              )}

              {/* ── FORM ── */}
              {phase === "form" && (
                <div className="ce-fade">

                  {/* Mode tabs */}
                  <div style={{ display: "flex", gap: 0, marginBottom: 24 }}>
                    {(["seller", "product"] as Mode[]).map(m => (
                      <button
                        key={m}
                        className={`ce-mode-btn${mode === m ? " active" : ""}`}
                        onClick={() => setMode(m)}
                      >
                        <span style={{ fontSize: 18 }}>{m === "seller" ? "🏪" : "📦"}</span>
                        {m === "seller" ? "SELLER SHOP" : "PRODUCT"}
                      </button>
                    ))}
                  </div>

                  {/* Info banner */}
                  <div style={{ background: T.goldBg, borderLeft: `3px solid ${T.gold}`, padding: "13px 16px", marginBottom: 24, fontSize: 12, color: "#7A5C00", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.7 }}>
                    <strong style={{ fontWeight: 700 }}>
                      {isSeller ? "Your electronics shop is featured on Areluxe." : "Your product is featured on Areluxe."}
                    </strong><br />
                    Enter your email and set a password to claim it free and go live instantly.
                  </div>

                  {/* Error */}
                  {errMsg && (
                    <div style={{ background: "#FFF5F5", border: `1px solid #FECACA`, padding: "11px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#8B0000", fontFamily: "'Montserrat', sans-serif" }}>✕ &nbsp;{errMsg}</span>
                    </div>
                  )}

                  {/* Email */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 7, textTransform: "uppercase" }}>
                      Email Address <span style={{ color: T.gold }}>✦</span>
                    </div>
                    <input
                      className="ce-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  {/* Password */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 7, textTransform: "uppercase" }}>
                      Create Password <span style={{ color: T.gold }}>✦</span>
                    </div>
                    <div style={{ position: "relative" }}>
                      <input
                        className="ce-input"
                        type={showPw ? "text" : "password"}
                        placeholder="Minimum 6 characters"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleClaim()}
                        style={{ paddingRight: 60 }}
                        autoComplete="new-password"
                      />
                      <button className="ce-pw-toggle" type="button" onClick={() => setShowPw(p => !p)}>
                        {showPw ? "HIDE" : "SHOW"}
                      </button>
                    </div>
                    {password.length > 0 && (
                      <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
                        {[1,2,3].map(i => (
                          <div key={i} style={{ flex: 1, height: 2, background: password.length >= i * 3 ? (password.length >= 8 ? T.gold : T.borderDk) : T.border, transition: "background .3s" }} />
                        ))}
                        <span style={{ fontSize: 9, color: T.muted, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em", marginLeft: 6, flexShrink: 0 }}>
                          {password.length < 3 ? "WEAK" : password.length < 8 ? "FAIR" : "STRONG"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <button className="ce-primary-btn" disabled={!canSubmit} onClick={handleClaim}>
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {isSeller ? "CLAIM MY SHOP — FREE" : "CLAIM MY PRODUCT — FREE"}
                  </button>

                  <p style={{ marginTop: 14, fontSize: 10, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", textAlign: "center", letterSpacing: "0.05em", lineHeight: 1.6 }}>
                    By claiming you agree to Areluxe's terms of service.<br />No credit card required.
                  </p>
                </div>
              )}

              {/* ── SUCCESS ── */}
              {phase === "success" && (
                <div className="ce-fade">

                  {/* Big tick */}
                  <div style={{ marginBottom: 28 }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", border: `2px solid ${T.black}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                      <svg width="22" height="18" viewBox="0 0 22 18" fill="none">
                        <path d="M1.5 9L8 15.5L20.5 2" stroke={T.black} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", color: T.muted, marginBottom: 10 }}>
                      {isSeller ? "SELLER ACCOUNT ACTIVE" : "PRODUCT LIVE"}
                    </p>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 20 : 24, fontWeight: 300, color: T.blackMid, lineHeight: 1.5 }}>
                      {isSeller
                        ? "Welcome to Areluxe Commerce. Your shop is live and ready for products."
                        : "Your product is now published on Areluxe. Buyers can find it immediately."
                      }
                    </p>
                  </div>

                  {/* Checklist */}
                  <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 22, marginBottom: 28 }}>
                    {(isSeller ? [
                      "Commerce seller account created",
                      "Shop profile live on Areluxe",
                      "List products from your dashboard",
                      "Reach buyers across multiple markets",
                    ] : [
                      "Product listing live on Areluxe",
                      "Seller account created automatically",
                      "Verified buyers can find & purchase it",
                      "Add more products from your dashboard",
                    ]).map((t, i) => (
                      <div key={i} className="ce-check-row">
                        <div className="ce-tick">
                          <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
                            <path d="M1 3.5L3 5.5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.6, paddingTop: 1 }}>{t}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="ce-primary-btn"
                    onClick={() => navigate("/MetaCommerceDashboard")}
                  >
                    GO TO DASHBOARD →
                  </button>
                </div>
              )}

              {/* ── ERROR ── */}
              {phase === "error" && (
                <div className="ce-fade">
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ width: 48, height: 48, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, fontSize: 20 }}>✕</div>
                    <p style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.8, marginBottom: 8 }}>
                      {errMsg || "This claim link is invalid or has already been used."}
                    </p>
                    <p style={{ fontSize: 11, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.7 }}>
                      If you believe this is an error, contact us at{" "}
                      <a href="mailto:marketing@saloisa.com" style={{ color: T.black, textDecoration: "underline" }}>marketing@saloisa.com</a>
                    </p>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <button className="ce-ghost-btn" onClick={() => navigate("/")}>← HOME</button>
                    <button className="ce-ghost-btn" onClick={() => navigate("/electronics-acquisition")}>NEW LISTING</button>
                  </div>
                </div>
              )}

            </div>

            {/* ── FOOTER ── */}
            <div style={{ borderTop: `1px solid ${T.border}`, background: T.bgMuted, padding: "18px 36px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: "0.25em", color: T.muted }}>ARELUXE</span>
              <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 9, color: T.mutedSoft, letterSpacing: "0.04em" }}>© 2025 Saloisa Group</span>
              <a href="mailto:marketing@saloisa.com?subject=STOP" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 9, color: T.mutedSoft, letterSpacing: "0.04em", textDecoration: "none" }}>Unsubscribe</a>
            </div>

          </div>

          {/* Bottom desktop step labels */}
          {phase === "form" && (
            <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 32 }}>
              {["Mode", "Account", "Live"].map((s, i) => (
                <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: i === 1 ? "rgba(255,255,255,.8)" : "rgba(255,255,255,.2)" }} />
                  <span style={{ fontSize: 8, color: i === 1 ? "rgba(255,255,255,.6)" : "rgba(255,255,255,.2)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.18em", fontWeight: 700 }}>{s}</span>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Claim;